import * as React from "react";
import { RootStore } from "../../../stores/root-store";
import { Icon, Input, Table } from "semantic-ui-react";
import { useLoaderData } from "react-router-dom";
import { useItemFilter } from "../../../hooks/use-item-filter";
import { useEffect } from "react";
import { UsageArea } from "../../../stores/area/area";
import { getLabel } from "../../../reflection/reflection";
import { AppState } from "../../../stores/app-mobx";
import { observer } from "mobx-react";

const searchTextKey = "report-usage-areas";
const sortAttributeKey = "report-usage-areas";

type LoadResult = {
    usageAreas: UsageArea[];
    appStore: AppState;
};

export async function loader(rootStore: RootStore, appStore: AppState) {
    const rootStoreSession = rootStore.newSession();
    const usageAreas = await rootStoreSession.areaStore.loadAllUsageAreas();

    return { usageAreas, appStore };
}

const ReportUsageAreas = observer(() => {
    const { usageAreas, appStore } = useLoaderData() as LoadResult;
    const searchText = appStore.searchTexts.get(searchTextKey) ?? "";
    const sortConfiguration = appStore.sortAttributes.get(sortAttributeKey) ?? {
        attribute: "id",
        direction: "ascending"
    };
    const [state, dispatch] = useItemFilter<UsageArea>(sortConfiguration.attribute, sortConfiguration.direction, usageAreas, []);
    const { sortAttribute, sortDirection, items } = state;

    useEffect(() => {
        appStore.setSortConfiguration(sortAttributeKey, { attribute: sortAttribute, direction: sortDirection });
    }, [sortAttribute, sortDirection]);

    return (
        <div>
            <Input
                id="search-field"
                placeholder="Suche nach ID"
                defaultValue={searchText}
                type="search"
                icon={
                    <Icon
                        name="delete"
                        link
                        onClick={() => {
                            const input = document.getElementById(
                                "search-field"
                            ) as HTMLInputElement;
                            input.value = "";
                            appStore.setSearchText(searchTextKey, "");
                        }}
                    />
                }
                onChange={(event) => appStore.setSearchText(searchTextKey, event.target.value)}
            />
            <Table celled={true} collapsing={true} compact="very" selectable={true} singleLine={true} size="small"
                   sortable={true}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell sorted={sortAttribute === "id" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "id" })}
                                          textAlign="center">
                            {getLabel(UsageArea.prototype, "id")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "projectId" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "projectId" })}
                                          textAlign="center">
                            {getLabel(UsageArea.prototype, "projectId")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "areaNEK2" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "areaNEK2" })}
                                          textAlign="right">
                            {getLabel(UsageArea.prototype, "areaNEK2")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "areaNEK3" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "areaNEK3" })}
                                          textAlign="right">
                            {getLabel(UsageArea.prototype, "areaNEK3")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "areaNEK4" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "areaNEK4" })}
                                          textAlign="right">
                            {getLabel(UsageArea.prototype, "areaNEK4")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "areaNEK5" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "areaNEK5" })}
                                          textAlign="right">
                            {getLabel(UsageArea.prototype, "areaNEK5")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "areaNEK6" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "areaNEK6" })}
                                          textAlign="right">
                            {getLabel(UsageArea.prototype, "areaNEK6")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "areaNEKUnknown" ? sortDirection : null}
                                          onClick={() => dispatch({
                                              type: "changeSort",
                                              sortAttribute: "areaNEKUnknown"
                                          })}
                                          textAlign="right">
                            {getLabel(UsageArea.prototype, "areaNEKUnknown")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "eligibility" ? sortDirection : null}
                                          onClick={() => dispatch({
                                              type: "changeSort",
                                              sortAttribute: "eligibility"
                                          })}
                                          textAlign="right">
                            {getLabel(UsageArea.prototype, "eligibilityDisplayText")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "effectiveAreaRounded" ? sortDirection : null}
                                          onClick={() => dispatch({
                                              type: "changeSort",
                                              sortAttribute: "effectiveAreaRounded"
                                          })}
                                          textAlign="right">
                            {getLabel(UsageArea.prototype, "effectiveAreaRounded")}
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {items.filter(p => p.id.toString().toLowerCase().includes(searchText.toLowerCase())).map((p) => (
                        <Table.Row key={p.id}>
                            <Table.Cell textAlign="center">{p.id}</Table.Cell>
                            <Table.Cell textAlign="center">{p.projectId}</Table.Cell>
                            <Table.Cell textAlign="right">{p.areaNEK2?.toLocaleString()}</Table.Cell>
                            <Table.Cell textAlign="right">{p.areaNEK3?.toLocaleString()}</Table.Cell>
                            <Table.Cell textAlign="right">{p.areaNEK4?.toLocaleString()}</Table.Cell>
                            <Table.Cell textAlign="right">{p.areaNEK5?.toLocaleString()}</Table.Cell>
                            <Table.Cell textAlign="right">{p.areaNEK6?.toLocaleString()}</Table.Cell>
                            <Table.Cell textAlign="right">{p.areaNEKUnknown?.toLocaleString()}</Table.Cell>
                            <Table.Cell textAlign="right">{p.eligibilityDisplayText}</Table.Cell>
                            <Table.Cell textAlign="right">{p.effectiveAreaRounded?.toLocaleString()}</Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </div>
    );
});

export default ReportUsageAreas;
